/**
 * AccessCodeInput Component
 * Reusable component for validating and applying access/discount codes
 */

import { useState } from 'react';
import { Input } from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import { Loader2, Ticket, CheckCircle2, XCircle, Percent, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@vsat/shared/lib/api';

export interface ValidatedCode {
  code: string;
  type: 'BYPASS' | 'DISCOUNT';
  discountType?: 'PERCENTAGE' | 'FIXED_AMOUNT';
  discountValue?: number;
  finalAmount: number;
  savingsAmount: number;
}

interface AccessCodeInputProps {
  onCodeApplied: (codeData: ValidatedCode) => void;
  onCodeRemoved: () => void;
  examId?: string;
  assessmentId?: string;
  systemContext?: 'VSAT' | 'THPT';
  originalAmount: number;
  disabled?: boolean;
}

export function AccessCodeInput({
  onCodeApplied,
  onCodeRemoved,
  examId,
  assessmentId,
  systemContext,
  originalAmount,
  disabled = false,
}: AccessCodeInputProps) {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validatedCode, setValidatedCode] = useState<ValidatedCode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateDiscount = (
    price: number,
    codeData: any
  ): { finalAmount: number; savings: number } => {
    if (codeData.type === 'BYPASS') {
      return { finalAmount: 0, savings: price };
    }

    let discount = 0;
    if (codeData.discountType === 'PERCENTAGE') {
      discount = Math.floor((price * codeData.discountValue) / 100);
    } else if (codeData.discountType === 'FIXED_AMOUNT') {
      discount = Math.min(codeData.discountValue, price);
    }

    const finalAmount = Math.max(0, price - discount);
    return { finalAmount, savings: discount };
  };

  const handleValidate = async () => {
    if (!code.trim()) {
      setError('Vui lòng nhập mã giảm giá');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const result = (await apiService.validateAccessCode(
        code.trim().toUpperCase(),
        examId,
        assessmentId,
        systemContext
      )) as any;

      const { finalAmount, savings } = calculateDiscount(originalAmount, result);

      const validatedData: ValidatedCode = {
        code: code.trim().toUpperCase(),
        type: result.type,
        discountType: result.discountType,
        discountValue: result.discountValue,
        finalAmount,
        savingsAmount: savings,
      };

      setValidatedCode(validatedData);
      onCodeApplied(validatedData);

      if (result.type === 'BYPASS') {
        toast.success('🎉 Mã miễn phí hợp lệ! Bạn không cần thanh toán.');
      } else {
        toast.success(
          `✅ Mã giảm giá hợp lệ! Tiết kiệm ${new Intl.NumberFormat('vi-VN').format(savings)}đ`
        );
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Mã giảm giá không hợp lệ hoặc đã hết hạn';
      setError(errorMessage);
      toast.error(errorMessage);
      setValidatedCode(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemove = () => {
    setCode('');
    setValidatedCode(null);
    setError(null);
    onCodeRemoved();
    toast.info('Đã xóa mã giảm giá');
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('vi-VN').format(price);

  return (
    <div className='space-y-3'>
      {/* Input Section */}
      {!validatedCode ? (
        <div className='space-y-2'>
          <label className='text-sm font-medium text-foreground flex items-center gap-2'>
            <Ticket className='h-4 w-4 text-primary' />
            Mã giảm giá (không bắt buộc)
          </label>
          <div className='flex gap-2'>
            <div className='flex-1'>
              <Input
                value={code}
                onChange={e => {
                  setCode(e.target.value.toUpperCase());
                  setError(null);
                }}
                placeholder='Nhập mã giảm giá'
                disabled={disabled || isValidating}
                className={error ? 'border-red-500 focus-visible:ring-red-500' : ''}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleValidate();
                  }
                }}
              />
              {error && (
                <p className='text-xs text-red-600 mt-1 flex items-center gap-1'>
                  <XCircle className='h-3 w-3' />
                  {error}
                </p>
              )}
            </div>
            <Button
              onClick={handleValidate}
              disabled={disabled || isValidating || !code.trim()}
              className='bg-primary hover:bg-primary/90'
            >
              {isValidating ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                  Kiểm tra
                </>
              ) : (
                'Áp dụng'
              )}
            </Button>
          </div>
        </div>
      ) : (
        /* Applied Code Display */
        <div className='rounded-lg border-2 border-success/40 bg-success/10 p-4'>
          <div className='flex items-start justify-between mb-3'>
            <div className='flex items-center gap-2'>
              <div className='h-10 w-10 rounded-full bg-success/20 flex items-center justify-center'>
                <CheckCircle2 className='h-5 w-5 text-success' />
              </div>
              <div>
                <p className='text-sm font-semibold text-foreground'>Mã giảm giá đã áp dụng</p>
                <code className='text-xs font-mono bg-white/50 px-2 py-0.5 rounded border border-success/30 text-foreground'>
                  {validatedCode.code}
                </code>
              </div>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleRemove}
              disabled={disabled}
              className='h-8 text-muted-foreground hover:text-foreground'
            >
              Xóa
            </Button>
          </div>

          {/* Discount Details */}
          <div className='space-y-2'>
            {validatedCode.type === 'BYPASS' ? (
              <Badge className='bg-success text-white border-success'>
                <Tag className='h-3 w-3 mr-1' />
                Miễn phí 100%
              </Badge>
            ) : (
              <div className='flex items-center gap-2'>
                <Badge variant='secondary' className='bg-primary/10 text-primary border-primary/30'>
                  {validatedCode.discountType === 'PERCENTAGE' ? (
                    <>
                      <Percent className='h-3 w-3 mr-1' />
                      Giảm {validatedCode.discountValue}%
                    </>
                  ) : (
                    <>
                      <Tag className='h-3 w-3 mr-1' />
                      Giảm {formatPrice(validatedCode.discountValue!)}đ
                    </>
                  )}
                </Badge>
              </div>
            )}

            <div className='pt-2 border-t border-success/20'>
              <div className='flex justify-between text-xs text-muted-foreground mb-1'>
                <span>Giá gốc:</span>
                <span className='line-through'>{formatPrice(originalAmount)}đ</span>
              </div>
              <div className='flex justify-between text-sm font-bold'>
                <span className='text-foreground'>Tiết kiệm:</span>
                <span className='text-success'>-{formatPrice(validatedCode.savingsAmount)}đ</span>
              </div>
              <div className='flex justify-between text-base font-bold mt-2 pt-2 border-t border-success/20'>
                <span className='text-foreground'>Tổng thanh toán:</span>
                <span className='text-success text-lg'>
                  {formatPrice(validatedCode.finalAmount)}đ
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
